/** A bridge's mDNS instance name must be its TXT `id` and nothing else: NsdManager's
 *  `lost` event carries only the instance name, so that is what the app deletes by. */
export const BRIDGE_SERVICE_TYPE = "_kinoq._tcp";

/** Numbers the discovery format only. */
export const DISCOVERY_VERSION = 1;

export const BRIDGE_NAME_MAX_BYTES = 63;

const ID_RE = /^[0-9a-f]{12}$/;
const V_RE = /^[1-9][0-9]{0,8}$/;

export interface BridgeTxt {
  id: string;
  name: string;
  v: number;
}

export interface DiscoveredBridge extends BridgeTxt {
  host: string;
  port: number;
}

export interface DiscoveryState {
  bridges: DiscoveredBridge[];
  /** Set when the browse itself failed. Never conflated with an empty list. */
  error?: string;
}

export interface BridgeDiscovery {
  start(): void;
  stop(): void;
  subscribe(listener: () => void): () => void;
  getState(): DiscoveryState;
}

function nameByteLength(name: string): number {
  return new TextEncoder().encode(name).length;
}

export function encodeBridgeTxt(txt: BridgeTxt): Record<string, string> {
  if (!ID_RE.test(txt.id)) {
    throw new Error(`bridge id must be 12 lowercase hex characters, got "${txt.id}"`);
  }
  const bytes = nameByteLength(txt.name);
  if (bytes === 0 || bytes > BRIDGE_NAME_MAX_BYTES) {
    throw new Error(`bridge name must be 1..${BRIDGE_NAME_MAX_BYTES} UTF-8 bytes, got ${bytes}`);
  }
  if (!Number.isInteger(txt.v) || txt.v < 1) {
    throw new Error(`bridge version must be a positive integer, got ${txt.v}`);
  }
  return { id: txt.id, name: txt.name, v: String(txt.v) };
}

export function decodeBridgeTxt(record: Record<string, string | undefined>): BridgeTxt | null {
  const { id, name, v } = record;
  if (id === undefined || !ID_RE.test(id)) return null;
  if (name === undefined) return null;
  const bytes = nameByteLength(name);
  if (bytes === 0 || bytes > BRIDGE_NAME_MAX_BYTES) return null;
  if (v === undefined || !V_RE.test(v)) return null;
  return { id, name, v: Number(v) };
}

/** A bridge numbering a format this build does not know is listed and disabled,
 *  never hidden — a reachable device must not look absent. */
export function isSpeakable(bridge: { v: number }): boolean {
  return bridge.v <= DISCOVERY_VERSION;
}
